
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from 'react';

import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef(null);
  const [lastEventIds, setLastEventIds] = useState([]);

  // Stable logout function shared with SocketContext.
  const logout = useCallback(() => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    setUser(null);
  }, []);

  // Check whether the current account is still active.
  const checkStatus = useCallback(async () => {
    if (
      !user ||
      user.role === 'super_admin' ||
      !sessionStorage.getItem('token')
    ) {
      return;
    }

    try {
      const response = await api.get('/auth/status');

      if (response.data.status === 'inactive') {
        logout();
      }
    } catch (err) {
      if (
        err.response?.status === 403 &&
        err.response?.data?.message === 'Account inactive'
      ) {
        logout();
        return;
      }

      console.error('Status check failed:', err);
    }
  }, [user, logout]);

  // Restore the existing session when the app loads.
  useEffect(() => {
    const loadUser = () => {
      try {
        const storedUser = sessionStorage.getItem('user');
        const token = sessionStorage.getItem('token');

        if (storedUser && token) {
          setUser(JSON.parse(storedUser));
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Error loading user:', err);
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // Poll account status every 15 seconds.
  useEffect(() => {
    if (!user || user.role === 'super_admin') {
      return undefined;
    }

    intervalRef.current = setInterval(() => {
      checkStatus();
    }, 15000);

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [user, checkStatus]);

  // Check status when the user returns to the browser tab.
  useEffect(() => {
    const onFocus = () => {
      if (user && user.role !== 'super_admin') {
        checkStatus();
      }
    };

    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [user, checkStatus]);

  // Login.
  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', {
        email,
        password,
      });

      const { token, user: loggedInUser } = response.data;

      sessionStorage.setItem('token', token);
      sessionStorage.setItem(
        'user',
        JSON.stringify(loggedInUser)
      );

      setUser(loggedInUser);

      return response;
    } catch (error) {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      throw error;
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
